/**
 * Shared to-do task helpers (status, lists, buckets, formatting, etc.).
 *
 * Do not import the Redux store or `store/reducer/to-do/extra` from here: `extra` already
 * depends on this module, so thunk/async flows belong in dedicated files (e.g.
 * `todo-brain-dump.util`). Post-merge slice sync lives in `todo-task-sync.util`.
 */
import {
  BRAIN_DUMP_TIMEOUT,
  EISENHOWER_QUADRANT,
  EMPTY_STRING,
  HEX_EMOJI_CODE_PATTERN,
  MAX_VISIBLE_TAGS,
  PERSPIRATION_LEVEL,
  STANDALONE_BRAIN_DUMP,
  TIMEOUT_DELAY,
  TIME_PRESSURE_SCORE,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import type {
  TodoStatusPagination,
  TodoTasksByStatus
} from 'interfaces/initial-states.interface';
import {
  FilterDataProps,
  MergedCustomStatusSection
} from 'interfaces/todos.interface';
import { decode } from 'html-entities';
import {
  CreateToDoPayload,
  DropDownSelectedValue,
  ExternalStatus,
  SubmitTaskPayload,
  SubTask,
  Tag,
  ToDoPlayerTask
} from 'interfaces';
import { TaskAssignee, ToDoTask } from 'interfaces/common.interface';
import { McpAgent } from 'interfaces/mcp-agent.interface';
import { Project } from 'interfaces/project.interface';
import moment from 'moment';
import {
  CHIP_VARIANT,
  SORT_MODE,
  TO_DO_STATUS,
  TODO_TASK_EXPANDED_SECTION
} from 'enums';
import {
  convertToSeconds,
  formatToDoPlayerTaskTimeConstraints,
  getTodoPlayerTaskDuration
} from './helpers';
import {
  getDateOptions,
  getOutcomeSteps,
  getPerspirationSteps,
  DEFAULT_TOP_OUTCOME_LEVEL,
  DEFAULT_TOP_PERSPIRATION_LEVEL,
  RANGE_SLIDER_STEPS
} from 'constants/topPrioritization';
import {
  formatToYMD,
  getTomorrowISODate,
  isDueDateOverdue,
  isDueDateToday
} from './time.util';
import { asArray, take } from 'utils/array.util';
import { uniqueByKey } from 'utils/unique-by-key.util';
import { clamp } from 'utils/number.util';
import { TaskFormData } from 'dashboard/to-do/task-drawer/TaskForm';

export const getEffectiveTaskStatus = (task: ToDoTask): string => {
  if (!task.external_task_id || !task.external_statuses?.length) {
    return task.status ?? TO_DO_STATUS.NOT_STARTED;
  }
  const matchingExternalStatus = task.external_statuses.find(
    (extStatus: ExternalStatus) => extStatus.label === task.status
  );
  if (matchingExternalStatus?.should_complete_task) {
    return TO_DO_STATUS.COMPLETED;
  }
  return task.status ?? TO_DO_STATUS.NOT_STARTED;
};

export const isExternalTaskCompleted = (task: ToDoTask): boolean => {
  return getEffectiveTaskStatus(task) === TO_DO_STATUS.COMPLETED;
};

export const areAllDurationsValid = (incomingTasks: SubmitTaskPayload[]) =>
  incomingTasks?.every((task) => hasTaskValidDuration(task));

export const hasTaskValidDuration = (task: SubmitTaskPayload) =>
  (task?.focusedDuration ?? 0) >= 0;

/**
 * Normalizes per-task playback flags before localStorage persist or desktop sync.
 *
 * `isPlaying` on a task means "this is the current player task", not necessarily
 * that the timer is running. `isPaused` reflects whether the selected task's timer
 * is stopped (`!isSelectedTaskPlaying`).
 *
 */
export const markPlayingTask = (
  tasks: ToDoPlayerTask[] | undefined,
  selectedTaskId?: string,
  isSelectedTaskPlaying = false
) => {
  return (tasks ?? []).map((task) => {
    // Completed tasks must never appear as playing (fixes stale flags from
    // localStorage, desktop sync, or completion paths that missed clearing them).
    if (task.status === TO_DO_STATUS.COMPLETED) {
      return {
        ...task,
        isPlaying: false,
        isPaused: false
      };
    }
    // No web selection: keep incoming flags (desktop / restored localStorage is
    // source of truth). Completed tasks are still cleared above.
    if (!selectedTaskId) {
      return task;
    }
    // Web knows the selected task: derive flags from selection + timer state
    // before sendDataToPlatform or localStorage write.
    const isSelected = task.id === selectedTaskId;
    return {
      ...task,
      isPlaying: isSelected,
      isPaused: isSelected ? !isSelectedTaskPlaying : false
    };
  });
};

export const normalizeTaskTitleKey = (title?: string | null) =>
  (title ?? '').trim().toLowerCase();

export const mergeTasksWithoutDuplicates = (tasks: ToDoTask[]) =>
  uniqueByKey(tasks, (task) => task.id, {
    normalizeKey: (key) => key,
    skipEmptyKeys: false
  });

/** Dedupe tasks in a list by normalized title (first wins). */
export const uniqueTasksByTitle = <T extends { title?: string | null }>(
  tasks: T[]
) =>
  uniqueByKey(tasks, (task) => task.title ?? '', {
    normalizeKey: normalizeTaskTitleKey
  });

/** Drop incoming tasks whose normalized title already exists in `existing`. */
export const excludeTasksWithDuplicateTitles = <
  T extends { title?: string | null }
>(
  incoming: T[],
  existing: T[]
) => {
  const existingKeys = new Set(
    existing.map((task) => normalizeTaskTitleKey(task.title))
  );
  return incoming.filter(
    (task) => !existingKeys.has(normalizeTaskTitleKey(task.title))
  );
};

export const getDateSevenDaysAgo = moment()
  .subtract(7, 'days')
  .format('YYYY-MM-DD');

export const eisenhowerQuadrantBackgroundColors = {
  [EISENHOWER_QUADRANT.DO]: '!bg-red-100 !dark:bg-red-900/30 !dark:bg-red-800',
  [EISENHOWER_QUADRANT.DECIDE]:
    '!bg-amber-100 !dark:bg-amber-900/30 !dark:bg-amber-800',
  [EISENHOWER_QUADRANT.DELEGATE]:
    '!bg-sky-100 !dark:bg-sky-900/30 !dark:bg-sky-800',
  [EISENHOWER_QUADRANT.DELETE]:
    '!bg-gray-100 !dark:bg-gray-800/50 !dark:bg-gray-800'
};

export const getPerspirationFilterOptions = (
  getLabel: (key: string) => string
): DropDownSelectedValue[] =>
  [
    {
      value: '',
      label: getLabel('to_do_procrastinate.all')
    },
    {
      value: PERSPIRATION_LEVEL.LOW,
      label: getLabel('to_do_procrastinate.low_spoons')
    },
    {
      value: PERSPIRATION_LEVEL.FULL,
      label: getLabel('to_do_procrastinate.full_spoons')
    }
  ].map((option) => ({
    ...option,
    subtitle:
      option.value === PERSPIRATION_LEVEL.LOW ||
      option.value === PERSPIRATION_LEVEL.FULL
        ? getLabel(
            option.value === PERSPIRATION_LEVEL.LOW
              ? 'to_do_procrastinate.perspiration_level_0_3'
              : 'to_do_procrastinate.perspiration_level_4_10'
          )
        : undefined
  }));

export const sanitizeInput = (rawInput?: string | null) => {
  if (!rawInput) {
    return EMPTY_STRING;
  }

  let decoded = rawInput;
  try {
    decoded = decodeURIComponent(decodeURIComponent(rawInput));
  } catch {
    try {
      decoded = decodeURIComponent(rawInput);
    } catch {
      // ignore if fails
    }
  }

  // Normalize and remove zero-width characters
  const decodeSpecialChars = (input: string) =>
    input
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013]/g, '-')
      .replace(/[\u2014]/g, '--')
      .replace(/\u2026/g, '...')
      .replace(/[\u00A0\u2002\u2003\u2007\u2009\u202F]/g, ' ');

  // Normalize and remove zero-width characters
  let cleaned = decoded.normalize('NFKC').replace(/[\u200B-\u200D\uFEFF]/g, '');

  // Ensure consistent newlines: convert \r\n or \r → \n
  cleaned = cleaned.replace(/\r\n?/g, '\n');

  // Decode HTML entities if needed
  return decode(decodeSpecialChars(cleaned));
};

// Ensures all line breaks are Windows-style CRLF (\r\n)
export const normalizeToCRLF = (input: string) => {
  // Safari < 16 does not support lookbehind. Convert any line ending to LF first,
  // then convert all LFs to CRLF. This achieves the same result as /(?<!\r)\n/.
  const normalizedToLF = input.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  return normalizedToLF.replace(/\n/g, '\r\n');
};

/** Buckets for accordion UX; preserves API order within each tier. */
export type TasksGroupedByEffectiveStatus = Record<TO_DO_STATUS, ToDoTask[]>;

/**
 * Groups tasks by effective status. Preserves input order within each bucket.
 */
export const groupTasksByEffectiveStatus = (
  tasks: ToDoTask[]
): TasksGroupedByEffectiveStatus => {
  const buckets: TasksGroupedByEffectiveStatus = {
    [TO_DO_STATUS.NOT_STARTED]: [],
    [TO_DO_STATUS.IN_PROGRESS]: [],
    [TO_DO_STATUS.COMPLETED]: []
  };

  for (const task of tasks) {
    const effective = getEffectiveTaskStatus(task);
    if (effective === TO_DO_STATUS.NOT_STARTED) {
      buckets[TO_DO_STATUS.NOT_STARTED].push(task);
    } else if (effective === TO_DO_STATUS.COMPLETED) {
      buckets[TO_DO_STATUS.COMPLETED].push(task);
    } else {
      buckets[TO_DO_STATUS.IN_PROGRESS].push(task);
    }
  }

  return buckets;
};

/**
 * Client-side filters for the todo list. Preserves incoming task order from the API (sort mode).
 * Status is expressed in the UI via grouping — not filtered here.
 */
export const getFilteredTasks = (
  tasks: ToDoTask[],
  {
    taskName,
    project,
    focusMode,
    perspirationLevel,
    dueDateRange
  }: FilterDataProps
) => {
  const filteredTasks = tasks.filter((task) => {
    const isNameMatched = task.title
      ? task.title.toLowerCase().includes(taskName.toLowerCase())
      : true;
    const isFocusModeMatched = focusMode
      ? task.focus_type === focusMode.value
      : true;
    const isProjectMatched = project
      ? (() => {
          const projectValue = String(project.value ?? '');
          const matchesTag =
            Array.isArray(task.tags) &&
            task.tags.some(
              (tag) =>
                String(tag.id) === projectValue || tag.text === project.label
            );
          const matchesProjectId =
            task.project_id != null && String(task.project_id) === projectValue;
          return matchesTag || matchesProjectId;
        })()
      : true;
    const due = task.due_date ? moment(task.due_date) : null;
    const hasDateRangeFilter =
      dueDateRange !== null &&
      dueDateRange !== undefined &&
      dueDateRange.startDate !== null &&
      dueDateRange.endDate !== null;
    const isInDateRange =
      hasDateRangeFilter &&
      !!due &&
      due.isBetween(
        moment(dueDateRange?.startDate),
        moment(dueDateRange?.endDate),
        'day',
        '[]'
      );

    const isDateFilterMatched = !hasDateRangeFilter || isInDateRange;

    let isPerspirationMatched = true;
    if (perspirationLevel === PERSPIRATION_LEVEL.LOW) {
      isPerspirationMatched =
        (task.perspiration_level ?? 0) >= 0 &&
        (task.perspiration_level ?? 0) <= 3;
    } else if (perspirationLevel === PERSPIRATION_LEVEL.FULL) {
      isPerspirationMatched =
        (task.perspiration_level ?? 0) >= 4 &&
        (task.perspiration_level ?? 0) <= 10;
    }

    return (
      isNameMatched &&
      isFocusModeMatched &&
      isProjectMatched &&
      isDateFilterMatched &&
      isPerspirationMatched
    );
  });

  return filteredTasks;
};

/** Apply {@link getFilteredTasks} to each API status bucket (client-side filters only). */
export const filterTasksByStatus = (
  tasksByStatus: TodoTasksByStatus,
  filters: FilterDataProps
): TodoTasksByStatus => ({
  [TO_DO_STATUS.IN_PROGRESS]: getFilteredTasks(
    tasksByStatus[TO_DO_STATUS.IN_PROGRESS],
    filters
  ),
  [TO_DO_STATUS.NOT_STARTED]: getFilteredTasks(
    tasksByStatus[TO_DO_STATUS.NOT_STARTED],
    filters
  ),
  [TO_DO_STATUS.COMPLETED]: getFilteredTasks(
    tasksByStatus[TO_DO_STATUS.COMPLETED],
    filters
  )
});

/**
 * Partition a flat array of tasks into status buckets for accordion rendering.
 * Tasks with an unrecognised status fall back to NOT_STARTED.
 */
export const bucketTasksByStatus = (tasks: ToDoTask[]): TodoTasksByStatus => {
  const buckets: TodoTasksByStatus = {
    [TO_DO_STATUS.IN_PROGRESS]: [],
    [TO_DO_STATUS.NOT_STARTED]: [],
    [TO_DO_STATUS.COMPLETED]: []
  };
  tasks.forEach((task) => {
    const status = task.status as TO_DO_STATUS;
    if (buckets[status]) {
      buckets[status].push(task);
    } else {
      buckets[TO_DO_STATUS.NOT_STARTED].push(task);
    }
  });
  return buckets;
};

/** Normalized key for merging cross-project custom status labels on the todos page. */
export const normalizeCustomStatusLabelKey = (label: string): string =>
  label.trim().toLowerCase();

const STATIC_DEFAULT_CUSTOM_LABEL_ALIASES: Record<
  TO_DO_STATUS,
  readonly string[]
> = {
  [TO_DO_STATUS.NOT_STARTED]: ['to do', 'todo', 'not started', 'not-started'],
  [TO_DO_STATUS.IN_PROGRESS]: [
    'doing',
    'in progress',
    'in-progress',
    'inprogress'
  ],
  [TO_DO_STATUS.COMPLETED]: ['done', 'completed', 'finished']
};

/** Case-insensitive map of custom status labels → default accordion bucket (todos page). */
export const buildDefaultCustomStatusAliasMap = (
  localizedLabels: Partial<Record<TO_DO_STATUS, string[]>> = {}
): Map<string, TO_DO_STATUS> => {
  const map = new Map<string, TO_DO_STATUS>();
  const add = (label: string, status: TO_DO_STATUS) => {
    const key = normalizeCustomStatusLabelKey(label);
    if (key) map.set(key, status);
  };

  (
    Object.entries(STATIC_DEFAULT_CUSTOM_LABEL_ALIASES) as [
      TO_DO_STATUS,
      readonly string[]
    ][]
  ).forEach(([status, labels]) => {
    labels.forEach((label) => add(label, status));
  });

  (
    Object.entries(localizedLabels) as [TO_DO_STATUS, string[] | undefined][]
  ).forEach(([status, labels]) => {
    labels?.forEach((label) => add(label, status));
  });

  return map;
};

export const resolveCustomStatusLabelToDefaultStatus = (
  label: string,
  aliasMap: Map<string, TO_DO_STATUS>
): TO_DO_STATUS | null =>
  aliasMap.get(normalizeCustomStatusLabelKey(label)) ?? null;

/** Bucket for a task that is not completed, used when its column says otherwise. */
const getOpenTaskStatus = (task: ToDoTask): TO_DO_STATUS =>
  task.status === TO_DO_STATUS.IN_PROGRESS
    ? TO_DO_STATUS.IN_PROGRESS
    : TO_DO_STATUS.NOT_STARTED;

/** Split custom-status tasks into default-bucket folds vs true custom accordion sections. */
export const partitionCustomTasksForTodosPage = (
  tasks: ToDoTask[],
  labelByStatusId: Map<string, string>,
  aliasMap: Map<string, TO_DO_STATUS>
): {
  foldedTasksByStatus: TodoTasksByStatus;
  tasksForCustomMerge: ToDoTask[];
} => {
  const foldedTasksByStatus: TodoTasksByStatus = {
    [TO_DO_STATUS.IN_PROGRESS]: [],
    [TO_DO_STATUS.NOT_STARTED]: [],
    [TO_DO_STATUS.COMPLETED]: []
  };
  const tasksForCustomMerge: ToDoTask[] = [];

  tasks.forEach((task) => {
    if (!task.custom_status_id) return;
    const label = labelByStatusId.get(task.custom_status_id);
    if (!label) return;

    const defaultStatus = resolveCustomStatusLabelToDefaultStatus(
      label,
      aliasMap
    );

    // Completing a task outside the project board (mobile, desktop, this page
    // before workflow sync) leaves custom_status_id on the old column, so the
    // column must never outrank the task's own completion state.
    if (task.status === TO_DO_STATUS.COMPLETED) {
      foldedTasksByStatus[TO_DO_STATUS.COMPLETED].push(task);
      return;
    }

    if (defaultStatus === TO_DO_STATUS.COMPLETED) {
      foldedTasksByStatus[getOpenTaskStatus(task)].push(task);
      return;
    }

    if (defaultStatus) {
      foldedTasksByStatus[defaultStatus].push(task);
      return;
    }

    tasksForCustomMerge.push(task);
  });

  return { foldedTasksByStatus, tasksForCustomMerge };
};

const appendUniqueTasks = (
  target: ToDoTask[],
  additions: ToDoTask[]
): ToDoTask[] => {
  const seen = new Set(target.map((task) => task.id));
  const uniqueAdditions = additions.filter((task) => !seen.has(task.id));
  return [...target, ...uniqueAdditions];
};

/**
 * Builds default status buckets for the todos accordion (excludes true customs, folds aliases).
 *
 * Folding appends alias custom-status tasks after the native ones, so the buckets must be
 * re-sorted after merging — otherwise a low-scoring native task can render above a high-scoring
 * folded task, defeating the fetch-time TOP sort. All buckets use the active sort mode.
 */
export const buildTodosPageDisplayTasksByStatus = (
  base: TodoTasksByStatus,
  foldedTasksByStatus: TodoTasksByStatus,
  customStatusTaskIds: Set<string>,
  sortMode: SORT_MODE
): TodoTasksByStatus => {
  const foldedTaskIds = new Set(
    [
      ...foldedTasksByStatus[TO_DO_STATUS.IN_PROGRESS],
      ...foldedTasksByStatus[TO_DO_STATUS.NOT_STARTED],
      ...foldedTasksByStatus[TO_DO_STATUS.COMPLETED]
    ].map((task) => task.id)
  );

  const stripNonDisplay = (tasks: ToDoTask[]) =>
    tasks.filter(
      (task) => !customStatusTaskIds.has(task.id) && !foldedTaskIds.has(task.id)
    );

  const mergeAndSort = (status: TO_DO_STATUS): ToDoTask[] =>
    sortTasksBySortMode(
      appendUniqueTasks(
        stripNonDisplay(base[status]),
        foldedTasksByStatus[status]
      ),
      sortMode
    );

  return {
    [TO_DO_STATUS.NOT_STARTED]: mergeAndSort(TO_DO_STATUS.NOT_STARTED),
    [TO_DO_STATUS.IN_PROGRESS]: mergeAndSort(TO_DO_STATUS.IN_PROGRESS),
    [TO_DO_STATUS.COMPLETED]: mergeAndSort(TO_DO_STATUS.COMPLETED)
  };
};

/**
 * Groups tasks with custom statuses by display label (case-insensitive) so identical
 * names from different projects appear in one accordion section on the global todos page.
 */
export const mergeCustomStatusSectionsByLabel = (
  tasks: ToDoTask[],
  labelByStatusId: Map<string, string>
): MergedCustomStatusSection[] => {
  const byKey = new Map<string, { label: string; tasks: ToDoTask[] }>();

  tasks.forEach((task) => {
    if (!task.custom_status_id) return;
    const label = labelByStatusId.get(task.custom_status_id);
    if (!label) return;

    const key = normalizeCustomStatusLabelKey(label);
    if (!key) return;

    const group = byKey.get(key);
    if (group) {
      group.tasks.push(task);
    } else {
      byKey.set(key, { label, tasks: [task] });
    }
  });

  return Array.from(byKey.entries())
    .map(([key, { label, tasks: sectionTasks }]) => ({
      id: key,
      label,
      tasks: sectionTasks
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const getTotalCountByStatus = (
  tasksByStatus: TodoTasksByStatus,
  filteredTasksByStatus: TodoTasksByStatus,
  todoStatusPagination: TodoStatusPagination
): Partial<Record<TO_DO_STATUS, number>> =>
  (
    [
      TO_DO_STATUS.IN_PROGRESS,
      TO_DO_STATUS.NOT_STARTED,
      TO_DO_STATUS.COMPLETED
    ] as const
  ).reduce(
    (acc, status) => {
      const filteredCount = filteredTasksByStatus[status].length;
      const totalCount = tasksByStatus[status].length;
      acc[status] =
        filteredCount === totalCount
          ? (todoStatusPagination[status]?.itemCount ?? totalCount)
          : filteredCount;
      return acc;
    },
    {} as Partial<Record<TO_DO_STATUS, number>>
  );

const resolveMetricLabel = (
  steps: {
    min: number;
    label: string;
  }[],
  value: number
): string => {
  // Clamp value into 1-10 and round to nearest integer
  const clamped = Math.max(1, Math.min(10, Math.round(value || 0)));
  let label = steps[0]?.label ?? '';

  for (const step of steps) {
    if (clamped >= step.min) {
      label = step.label;
    } else {
      break;
    }
  }

  return label;
};

const daysUntilDue = (due: Date | string | number, now = new Date()) => {
  const MS_PER_DAY = TIME_UNIT_CONVERT_FACTOR.ONE_DAY_AS_MILLISECONDS;
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  // Rounded because a DST change inside the span shifts the difference by an
  // hour, which otherwise leaks a fractional day into the TOP score.
  return Math.round(
    (startOfDay(new Date(due)).getTime() - startOfDay(now).getTime()) /
      MS_PER_DAY
  );
};

export const workloadShareOfRunway = (effortMinutes: number, days: number) => {
  const NUMERIC_STABILITY_EPS = 1e-9;
  const EFFORT_MINUTES_PER_RUNWAY_DAY = 60;

  const minutes = Math.max(0, Number(effortMinutes) || 0);
  const runwayMinutes = Math.max(0, days) * EFFORT_MINUTES_PER_RUNWAY_DAY;
  return minutes / Math.max(NUMERIC_STABILITY_EPS, minutes + runwayMinutes);
};

export const baseUrgency = (days: number): number => {
  if (!Number.isFinite(days) || days < 0) {
    return 8;
  }
  const raw = 8 - Math.log(days + 1) * 1.5;
  // Match BE ToDoTopScoreService / time-pressure domain (floor 0.1).
  return Math.max(TIME_PRESSURE_SCORE.SCORE_FLOOR, raw);
};

export const timePressureScore = (
  due?: Date | string | null,
  effortMinutes?: number | null
): number => {
  if (!due) {
    return TIME_PRESSURE_SCORE.SCORE_FLOOR;
  }

  const dueDate = new Date(due as string | number | Date);
  if (isNaN(dueDate.getTime())) {
    return TIME_PRESSURE_SCORE.SCORE_FLOOR;
  }

  const days = daysUntilDue(dueDate);

  // Match BE: overdue time pressure grows with days past due.
  if (days < 0) {
    return TIME_PRESSURE_SCORE.SCORE_PAST_DUE + Math.abs(days);
  }

  if (days === 0) {
    return TIME_PRESSURE_SCORE.SCORE_TODAY;
  }

  const safeEffort = Number(effortMinutes);
  if (!Number.isFinite(safeEffort) || safeEffort <= 0) {
    return TIME_PRESSURE_SCORE.SCORE_FLOOR;
  }

  const base = baseUrgency(days);
  const workload = workloadShareOfRunway(safeEffort, days);

  // Match mobile/BE: (baseUrgency / 8) × 10 × workloadShare
  const score = (base / 8) * 10 * workload;
  return Math.min(10, Math.max(TIME_PRESSURE_SCORE.SCORE_FLOOR, score));
};

/**
 * Maps perspiration level (1–10) to effort minutes.
 * Must match BE ToDoTopScoreService / domain time-pressure mapping.
 */
export const mapPerspirationLevelToMinutes = (level: number) => {
  const mapping: { [threshold: number]: number } = {
    1: 5, // 5 minutes
    2: 15, // 15 minutes
    3: 30, // 30 minutes
    4: 60, // 1 hour
    5: 240, // 4 hours
    6: 240, // 4 hours
    7: 480, // 8 hours
    8: 480, // 8 hours
    9: 3360, // 56 hours
    10: 3360 // 56 hours
  };

  if (level >= 1 && level <= 10) {
    return mapping[level];
  }

  return mapping[1];
};

/** Converts perspiration effort minutes into task `duration` seconds. */
export const getDurationSecondsFromPerspiration = (
  perspirationLevel?: number
): number => {
  const clampedLevel = clamp(
    Number(perspirationLevel) || DEFAULT_TOP_PERSPIRATION_LEVEL,
    RANGE_SLIDER_STEPS.MIN,
    RANGE_SLIDER_STEPS.MAX
  );
  return convertToSeconds(mapPerspirationLevelToMinutes(clampedLevel));
};

/**
 * Prefer an explicit duration (e.g. player TimeDuration input or existing task).
 * Otherwise derive seconds from perspiration for create flows without a duration field.
 */
export const resolveTaskDurationSeconds = ({
  explicitDuration,
  perspirationLevel
}: {
  explicitDuration?: number | null;
  perspirationLevel?: number;
}): number => {
  if (explicitDuration != null && Number(explicitDuration) > 0) {
    return Number(explicitDuration);
  }
  return getDurationSecondsFromPerspiration(perspirationLevel);
};

/**
 * Calculates Time Pressure (T) value from perspiration level and due date.
 * Used in TOP formula: TOP = T × O / P
 */
export const computeTimePressure = (
  perspirationLevel: number,
  dueDate?: string | Date | null
): number => {
  const clampedPerspiration = Math.max(
    1,
    Math.min(10, Number(perspirationLevel) || 1)
  );

  const effortMinutes = mapPerspirationLevelToMinutes(clampedPerspiration);
  return timePressureScore(dueDate, effortMinutes);
};

export const computeTopScorePreview = (
  outcome: number,
  perspirationLevel: number,
  dueDate?: string
): number => {
  const safeOutcome = Number(outcome) || 0;
  const clampedPerspiration = Math.max(
    1,
    Math.min(10, Number(perspirationLevel) || 1)
  );

  const effortMinutes = mapPerspirationLevelToMinutes(clampedPerspiration);
  const timePressure = timePressureScore(dueDate, effortMinutes);

  return (timePressure * safeOutcome) / clampedPerspiration;
};

export const getTOPScore = (task: {
  top_score?: number;
  due_date?: string | Date | null;
  outcome?: number;
  perspiration_level?: number;
  status?: string;
}): number => {
  // Previously returned 0 for completed so they would not compete with active
  // tasks in Smart/TOP ranking. We now keep the stored/computed score so
  // completed tasks still show their historical TOP in the UI.
  if (typeof task.top_score === 'number') {
    return task.top_score;
  }

  const outcome = Number(task.outcome) || 0;
  const perspirationLevel = Math.max(
    1,
    Math.min(10, Number(task.perspiration_level) || 1)
  );

  const effortMinutes = mapPerspirationLevelToMinutes(perspirationLevel);
  const timePressure = timePressureScore(task?.due_date ?? null, effortMinutes);

  return (timePressure * outcome) / perspirationLevel;
};

/**
 * Sentinel so tasks without a due date sort after all dated tasks in chronological mode.
 * Infinity (not a far-future date) ensures any real due date sorts first; the comparator
 * must treat equal values explicitly because Infinity - Infinity is NaN.
 */
const NO_DUE_DATE_SORT_VALUE = Number.POSITIVE_INFINITY;

const getDueDateSortValue = (task: ToDoTask): number => {
  if (!task.due_date) {
    return NO_DUE_DATE_SORT_VALUE;
  }
  const time = new Date(task.due_date).getTime();
  return Number.isNaN(time) ? NO_DUE_DATE_SORT_VALUE : time;
};

/**
 * Due-date order: earliest due date first, so overdue tasks surface at the top,
 * then soonest due. Tasks without a due date go last. Stable for equal dates.
 */
export const sortTasksByDueDate = (tasks: ToDoTask[]): ToDoTask[] =>
  [...tasks].sort((taskA, taskB) => {
    const valA = getDueDateSortValue(taskA);
    const valB = getDueDateSortValue(taskB);
    return valA === valB ? 0 : valA - valB;
  });

/** Chronological order: most recently created first. */
export const sortTasksByUpdatedAt = (tasks: ToDoTask[]): ToDoTask[] =>
  [...tasks].sort((taskA, taskB) => {
    const timeA = getUpdatedAtSortValue(taskA);
    const timeB = getUpdatedAtSortValue(taskB);
    return timeB - timeA;
  });

const getUpdatedAtSortValue = (task: ToDoTask): number => {
  const raw = task.created_at || '';
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/** Smart/TOP order: highest TOP score first. Stable for equal scores. */
export const sortTasksByTopScore = (tasks: ToDoTask[]): ToDoTask[] =>
  [...tasks].sort((a, b) => getTOPScore(b) - getTOPScore(a));

/** Orders a task list to match the active sort toolbar mode. */
export const sortTasksBySortMode = (
  tasks: ToDoTask[],
  sortMode: SORT_MODE
): ToDoTask[] => {
  if (sortMode === SORT_MODE.DUE_DATE) {
    return sortTasksByDueDate(tasks);
  }
  if (sortMode === SORT_MODE.CHRONOLOGICAL) {
    return sortTasksByUpdatedAt(tasks);
  }
  return sortTasksByTopScore(tasks);
};

export const getOutcomeLabelFromScore = (value: number): string =>
  resolveMetricLabel(getOutcomeSteps(), value);

export const getPerspirationLabelFromScore = (value: number): string =>
  resolveMetricLabel(getPerspirationSteps(), value);

export const secondsToDurationLabel = (seconds?: number): string => {
  const totalSeconds = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(totalSeconds / 3600);
  const remainderAfterHours = totalSeconds % 3600;
  const minutes = Math.floor(remainderAfterHours / 60);
  const secs = remainderAfterHours % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(secs).padStart(2, '0');

  if (hours > 0) {
    return `${hours}h:${mm}m:${ss}s`;
  }

  return `${mm}m:${ss}s`;
};

export const refineTaskTopMetrics = (task: ToDoTask) => {
  const refined: ToDoTask = { ...task };

  const outcome = Number(task.outcome) ?? 0;
  const perspirationLevel = Math.max(
    1,
    Math.min(10, Number(task.perspiration_level) || 1)
  );

  const topScore = getTOPScore({
    top_score: refined.top_score,
    due_date: refined.due_date,
    outcome,
    perspiration_level: perspirationLevel,
    status: refined.status
  });

  return {
    ...refined,
    top_score: topScore,
    outcome,
    perspiration_level: perspirationLevel
  };
};

export const partitionTaskTags = (
  tags: Tag[],
  projects: Project[]
): { projectTags: Tag[]; labelTags: Tag[] } => {
  const projectIds = new Set(projects.map((project) => project.id));
  const projectTags: Tag[] = [];
  const labelTags: Tag[] = [];

  tags.forEach((tag) => {
    if (projectIds.has(tag.id)) {
      projectTags.push(tag);
    } else {
      labelTags.push(tag);
    }
  });

  return { projectTags, labelTags };
};

export const isSyncedProject = (project: Project): boolean =>
  Boolean(project.synced_project_id?.length);

/** Query params for GET /to-do when the todos list project filter is active. */
export const buildGetToDosProjectParams = (
  projectFilter: DropDownSelectedValue | null | undefined,
  projects: Project[] = []
): { project_id?: string; synced_project_id?: string } => {
  if (!projectFilter?.value || String(projectFilter.value) === '') {
    return {};
  }
  const projectId = String(projectFilter.value);
  const project = projects.find((p) => p.id === projectId);
  if (project?.synced_project_id) {
    return { synced_project_id: project.synced_project_id };
  }
  return { project_id: projectId };
};

/** Native projects with workflow columns; synced/external projects use global status. */
export const projectUsesCustomStatuses = (
  project: Project | undefined
): boolean => {
  if (!project || isSyncedProject(project)) {
    return false;
  }
  return (project.custom_statuses?.length ?? 0) > 0;
};

export const resolveStatusOwnerProjectId = ({
  projectTags,
  projects,
  statusOwnerProjectId,
  customStatusId
}: {
  projectTags: Tag[];
  projects: Project[];
  statusOwnerProjectId?: string | null;
  customStatusId?: string | null;
}): string | null => {
  if (projectTags.length === 0) {
    return null;
  }

  const projectTagIds = new Set(projectTags.map((tag) => tag.id));

  if (statusOwnerProjectId && projectTagIds.has(statusOwnerProjectId)) {
    return statusOwnerProjectId;
  }

  if (customStatusId) {
    const ownerFromCustomStatus = projects.find(
      (project) =>
        projectTagIds.has(project.id) &&
        projectUsesCustomStatuses(project) &&
        project.custom_statuses?.some((status) => status.id === customStatusId)
    );
    if (ownerFromCustomStatus) {
      return ownerFromCustomStatus.id;
    }
  }

  const nativeWithCustom = projectTags.find((tag) => {
    const project = projects.find((entry) => entry.id === tag.id);
    return projectUsesCustomStatuses(project);
  });
  if (nativeWithCustom) {
    return nativeWithCustom.id;
  }

  return projectTags[0]?.id ?? null;
};

export const getCustomStatusesForStatusOwner = (
  statusOwnerProjectId: string | null,
  projects: Project[]
) => {
  if (!statusOwnerProjectId) {
    return [];
  }
  const project = projects.find((entry) => entry.id === statusOwnerProjectId);
  if (!projectUsesCustomStatuses(project)) {
    return [];
  }
  return [...(project?.custom_statuses ?? [])].sort(
    (a, b) => a.order - b.order
  );
};

export const buildTaskDrawerStatusPayload = (
  formData: TaskFormData,
  projects: Project[]
): Pick<
  CreateToDoPayload,
  'tags' | 'project_id' | 'custom_status_id' | 'status'
> => {
  const { projectTags, labelTags } = partitionTaskTags(
    formData.project,
    projects
  );
  const project_id = resolveStatusOwnerProjectId({
    projectTags,
    projects,
    statusOwnerProjectId: formData.status_owner_project_id,
    customStatusId: formData.custom_status_id
  });
  const ownerProject = projects.find((project) => project.id === project_id);
  const useCustomStatuses = projectUsesCustomStatuses(ownerProject);

  return {
    tags: [...labelTags, ...projectTags],
    project_id: project_id ?? undefined,
    custom_status_id: useCustomStatuses
      ? (formData.custom_status_id ?? null)
      : null,
    status: formData.status
  };
};

/**
 * Unassigning must go out as an explicit `null`: an `undefined` assignee is
 * dropped from the JSON body, and the API reads a missing key as "no change".
 * It is sent only when the form started with a human assignee — a form that
 * never knew the assignee (a read without the relation) must not erase it.
 * Picking an agent leaves the human assignee untouched.
 */
export const buildAssigneePayloadFields = ({
  assignee,
  initialAssignee
}: {
  assignee?: TaskAssignee | null;
  initialAssignee?: TaskAssignee | null;
}): Pick<ToDoTask, 'assignee_id' | 'assigned_mcp_token_id'> => {
  if (assignee?.isAgent) {
    return { assignee_id: undefined, assigned_mcp_token_id: assignee.id };
  }
  if (assignee) {
    return { assignee_id: assignee.id, assigned_mcp_token_id: null };
  }
  const isUnassigningHuman = Boolean(initialAssignee) && !initialAssignee?.isAgent;
  return {
    assignee_id: isUnassigningHuman ? null : undefined,
    assigned_mcp_token_id: null
  };
};

export const mapTaskToFormData = (
  task: ToDoTask,
  mcpAgents: McpAgent[] = []
): TaskFormData => {
  // Resolve agent assignee if task was assigned to an MCP agent
  let resolvedAssignee = task.assignee || null;
  if (task.assigned_mcp_token_id && !resolvedAssignee) {
    const agent = mcpAgents.find((a) => a.id === task.assigned_mcp_token_id);
    if (agent) {
      resolvedAssignee = {
        id: agent.id,
        name: agent.agent_name || agent.label || 'AI Agent',
        isAgent: true
      };
    }
  }

  return {
    isEditMode: true,
    title: task.title || '',
    details: task.details || '',
    outcome: {
      value: task.outcome ?? DEFAULT_TOP_OUTCOME_LEVEL,
      label: getOutcomeLabelFromScore(task.outcome ?? DEFAULT_TOP_OUTCOME_LEVEL)
    },
    perspiration: {
      value: task.perspiration_level ?? DEFAULT_TOP_PERSPIRATION_LEVEL,
      label: getPerspirationLabelFromScore(
        task.perspiration_level ?? DEFAULT_TOP_PERSPIRATION_LEVEL
      )
    },
    project: task.tags || [],
    status_owner_project_id: null,
    timeline: {
      topScore: task.top_score || 0,
      due_date: task.due_date
        ? formatToYMD(task.due_date)
        : getTomorrowISODate()
    },
    expandedSection: TODO_TASK_EXPANDED_SECTION.COMMENTS,
    previousSection: TODO_TASK_EXPANDED_SECTION.NONE,
    status: (task.status as TO_DO_STATUS) || TO_DO_STATUS.NOT_STARTED,
    custom_status_id: task.custom_status_id ?? null,
    assignee: resolvedAssignee,
    subtasks: task.subtasks || [],
    pendingSubtaskName: ''
  };
};

export const appendPendingSubtaskToList = (
  subtasks: SubTask[],
  pendingName: string
): SubTask[] => {
  const trimmed = pendingName.trim();
  if (!trimmed) return subtasks;

  return [...subtasks, { name: trimmed, is_completed: false }];
};

export const getSelectedDueDateLabel = (
  dueDate: string | undefined
): string | null => {
  if (!dueDate) return null;

  const selectedOption = getDateOptions().find((option) => {
    if (!option.getDate) return false;
    const optionDate = option.getDate();
    const optionKey = formatToYMD(optionDate);
    return dueDate === optionKey;
  });

  return selectedOption?.label ?? null;
};

export const hasTaskFormDataChanged = (
  original: TaskFormData,
  current: TaskFormData
): boolean => {
  return (
    original.title !== current.title ||
    original.details !== current.details ||
    original.outcome.value !== current.outcome.value ||
    original.perspiration.value !== current.perspiration.value ||
    original.timeline.due_date !== current.timeline.due_date ||
    original.status !== current.status ||
    original.custom_status_id !== current.custom_status_id ||
    original.status_owner_project_id !== current.status_owner_project_id ||
    original.assignee?.id !== current.assignee?.id ||
    JSON.stringify(original.project) !== JSON.stringify(current.project) ||
    JSON.stringify(original.subtasks) !== JSON.stringify(current.subtasks) ||
    original.pendingSubtaskName !== current.pendingSubtaskName
  );
};

export const filterNewTasks = (
  existingTasks: ToDoTask[],
  newTasks: ToDoTask[]
): ToDoTask[] => {
  const existingTaskIds = new Set(existingTasks.map((todoTask) => todoTask.id));
  return newTasks.filter((todoTask) => !existingTaskIds.has(todoTask.id));
};

export const formatTasksForPlayer = (tasks: ToDoTask[]): ToDoPlayerTask[] => {
  const playerTasks: ToDoPlayerTask[] = tasks.map((task) => ({
    ...task,
    startTime: '',
    endTime: '',
    focusedDuration: 0,
    duration: getTodoPlayerTaskDuration(task.duration),
    isPlaying: false,
    isPaused: false
  }));

  return formatToDoPlayerTaskTimeConstraints(playerTasks);
};

export const validateTaskInputs = (tasks: ToDoPlayerTask[]) => {
  return (
    tasks.length > 0 &&
    tasks.every(
      (task) =>
        task.title.trim().length > 0 && task.duration && task.duration > 0
    )
  );
};

export const isStandaloneBrainDumpInputVague = (input: string): boolean => {
  const trimmedInput = input.trim();
  const wordCount = trimmedInput.split(/\s+/).filter(Boolean).length;
  return (
    wordCount < STANDALONE_BRAIN_DUMP.MIN_WORDS_FOR_CLEAR_INPUT ||
    trimmedInput.length < STANDALONE_BRAIN_DUMP.MIN_CHARS_FOR_CLEAR_INPUT
  );
};

export const getBrainDumpTimeout = (length: number): number => {
  if (length <= BRAIN_DUMP_TIMEOUT.SHORT_LENGTH) {
    return TIMEOUT_DELAY.AXIOS;
  }
  if (length <= BRAIN_DUMP_TIMEOUT.MEDIUM_LENGTH) {
    return BRAIN_DUMP_TIMEOUT.MEDIUM_TIMEOUT;
  } else {
    return BRAIN_DUMP_TIMEOUT.LONG_TIMEOUT;
  }
};

const TODO_BUCKET_KEYS: TO_DO_STATUS[] = [
  TO_DO_STATUS.IN_PROGRESS,
  TO_DO_STATUS.NOT_STARTED,
  TO_DO_STATUS.COMPLETED
];

export function rebuildMergedTasksFromBuckets(state: {
  tasks: ToDoTask[];
  tasksByStatus: TodoTasksByStatus;
}) {
  state.tasks = [
    ...state.tasksByStatus[TO_DO_STATUS.IN_PROGRESS],
    ...state.tasksByStatus[TO_DO_STATUS.NOT_STARTED],
    ...state.tasksByStatus[TO_DO_STATUS.COMPLETED]
  ];
}

export function removeTaskAcrossBuckets(
  state: {
    tasks: ToDoTask[];
    tasksByStatus: TodoTasksByStatus;
    todoStatusPagination?: TodoStatusPagination;
  },
  taskId: string
) {
  for (const s of TODO_BUCKET_KEYS) {
    const ix = state.tasksByStatus[s].findIndex((t) => t.id === taskId);
    if (ix === -1) continue;
    state.tasksByStatus[s].splice(ix, 1);
    const pagination = state.todoStatusPagination?.[s];
    if (pagination) {
      pagination.itemCount = Math.max(0, pagination.itemCount - 1);
    }
  }
  rebuildMergedTasksFromBuckets(state);
}

export function upsertTaskAcrossBuckets(
  state: {
    tasks: ToDoTask[];
    tasksByStatus: TodoTasksByStatus;
    todoStatusPagination?: TodoStatusPagination;
  },
  task: ToDoTask
) {
  let previousBucket: TO_DO_STATUS | null = null;
  for (const s of TODO_BUCKET_KEYS) {
    const ix = state.tasksByStatus[s].findIndex((t) => t.id === task.id);
    if (ix !== -1) {
      previousBucket = s;
      state.tasksByStatus[s].splice(ix, 1);
    }
  }
  let bucket = getEffectiveTaskStatus(task) as TO_DO_STATUS;
  if (!TODO_BUCKET_KEYS.includes(bucket)) {
    bucket = TO_DO_STATUS.IN_PROGRESS;
  }
  state.tasksByStatus[bucket].push(task);
  // Keep the per-status total counts (used by the accordion section headers,
  // e.g. "Doing (7)") in sync when a task moves between buckets so the header
  // reflects the live count rather than the stale server-reported total.
  if (state.todoStatusPagination && previousBucket !== bucket) {
    const pagination = state.todoStatusPagination;
    if (previousBucket && pagination[previousBucket]) {
      pagination[previousBucket].itemCount = Math.max(
        0,
        pagination[previousBucket].itemCount - 1
      );
    }
    if (pagination[bucket]) {
      pagination[bucket].itemCount += 1;
    }
  }
  rebuildMergedTasksFromBuckets(state);
}

export const getSortChipClassName = (isActive: boolean, base = ''): string =>
  `${base} transition-transform shrink-0 ${
    isActive
      ? 'scale-105 font-semibold ring-1 ring-primary-400 bg-primary-50'
      : 'opacity-80'
  }`.trim();

/** Converts API hex unified codes (e.g. `1f600`) to emoji glyphs. */
export const resolveTaskIconEmoji = (icon?: string | null): string => {
  const trimmed = icon?.trim() ?? '';
  if (trimmed.length === 0) {
    return '';
  }
  if (!HEX_EMOJI_CODE_PATTERN.test(trimmed)) {
    return trimmed;
  }
  try {
    return trimmed
      .split('-')
      .map((part) => String.fromCodePoint(parseInt(part, 16)))
      .join('');
  } catch {
    return trimmed;
  }
};

export const getSubtaskHoverMessage = (task: ToDoTask): string =>
  asArray<SubTask>(task.subtasks)
    .map((subtask) => `• ${decode(subtask.name)}`)
    .join('\n');

export const getExtraTagsHoverMessage = (task: ToDoTask): string => {
  const tags = asArray<Tag>(task.tags);
  return take(
    tags,
    Math.max(0, tags.length - MAX_VISIBLE_TAGS),
    MAX_VISIBLE_TAGS
  )
    .map((tag) => tag.text)
    .join('\n');
};

export const getDueDateChipVariant = (
  task: ToDoTask
): CHIP_VARIANT | undefined => {
  if (!task.due_date || task.status === TO_DO_STATUS.COMPLETED) {
    return undefined;
  }
  if (isDueDateOverdue(task.due_date)) {
    return CHIP_VARIANT.DANGER;
  }
  if (isDueDateToday(task.due_date)) {
    return CHIP_VARIANT.WARNING;
  }
  return undefined;
};
